import { createApiClient, type RefreshResult } from "@aeriosiq/api-client";
import {
  authStore,
  pickConfigValue,
  readRuntimeConfig,
  tryRefresh,
} from "@aeriosiq/auth-client";

// Go-live 3.1 — the container-injected runtime config (window.__AIQ_CONFIG__,
// loaded by /config.js before any module executes) wins over the build-time
// value. The VITE_API_BASE_URL read MUST stay a literal property access:
// Vite's `define` substitution only rewrites literal `import.meta.env.X`
// expressions, and dev (DEV_API_MODE=realbe) relies on that substitution to
// point this client at the absolute :3005 gateway URL.
const baseURL =
  pickConfigValue(
    readRuntimeConfig().apiBaseUrl,
    import.meta.env.VITE_API_BASE_URL as string | undefined,
  ) ?? "/api/lyse/v1";

export const api = createApiClient({
  baseURL,
  // Story 009 — read live tokens + tenant from the auth store.
  getAuthToken: () => authStore.get().session?.accessToken ?? null,
  getTenantId: () => authStore.get().tenant?.tenantSlug ?? null,
  // SB6-AUTH-RECONNECT-01 — refresh-on-401 now delegates to the shared
  // `tryRefresh()` helper in @aeriosiq/auth-client. That helper:
  //   - dedupes parallel refresh exchanges across BOTH the axios
  //     interceptor and the raw-fetch wrapper (`fetchWithRefresh`)
  //     and the proactive timer (`useProactiveTokenRefresh`)
  //   - writes the rotated token pair + new `accessTokenExpiresAt`
  //     into the auth store
  //   - on failure: routes to `markSessionExpired()` (soft modal) for
  //     idle users or `clearSession()` (legacy bounce-to-login) for
  //     active users, gated by `IDLE_MAX_MS` and the 30-day hard cap
  refreshAuthToken: async (): Promise<RefreshResult | null> => {
    const accessToken = await tryRefresh();
    if (!accessToken) return null;
    return { accessToken };
  },
});
